import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Home, 
    Search, 
    Users, 
    Mail, 
    UsersRound, 
    User, 
    LogOut,
    Moon,
    Sun,
    Bell,
    Menu,
    X,
    LogIn,
    Shield,
    Coins,
    Newspaper,
    Check,
    Gift
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { AuthModal } from '../auth/AuthModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
    { icon: Home, label: 'Strona główna', href: '/', badge: null, requiresAuth: false },
    { icon: Search, label: 'Szukaj klasy', href: '/search', badge: null, requiresAuth: false },
    { icon: Newspaper, label: 'Blog', href: '/blog', badge: null, requiresAuth: false },
    { icon: Users, label: 'Znajomi', href: '/friends', badge: null, requiresAuth: true },
    { icon: Mail, label: 'Wiadomości', href: '/messages', badge: null, requiresAuth: true },
    { icon: UsersRound, label: 'Grupy', href: '/groups', badge: null, requiresAuth: true },
    { icon: User, label: 'Profil', href: '/profile', badge: null, requiresAuth: true },
];

export const Header = () => {
    const { isDark, toggleTheme } = useTheme();
    const { user, isAuthenticated, logout, isLoading } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authTab, setAuthTab] = useState('login');
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const navigate = useNavigate();

    // Fetch notifications
    useEffect(() => {
        if (!isAuthenticated) return;
        
        const fetchNotifications = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/api/notifications?limit=10`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setNotifications(data.notifications || []);
                    setUnreadCount(data.unread_count || 0);
                }
            } catch (error) {
                console.error('Error fetching notifications:', error);
            }
        };
        
        fetchNotifications();
        // Refresh every 30 seconds
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [isAuthenticated]);

    const markAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/api/notifications/mark-read/${notificationId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setNotifications(prev => prev.map(n => 
                n.id === notificationId ? { ...n, read: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/api/notifications/mark-all-read`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const handleNotificationClick = (notif) => {
        if (!notif.read) {
            markAsRead(notif.id);
        }
        if (notif.link) {
            navigate(notif.link);
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'reward': return '⭐';
            case 'friend': return '👥';
            case 'message': return '💬';
            case 'gift': return '🎁';
            default: return '📢';
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);
        
        if (minutes < 1) return 'teraz';
        if (minutes < 60) return `${minutes} min temu`;
        if (hours < 24) return `${hours} godz. temu`;
        if (days < 7) return `${days} dni temu`;
        return date.toLocaleDateString('pl-PL');
    };

    const handleLogout = async () => {
        await logout();
    };

    const openLogin = () => {
        setAuthTab('login');
        setShowAuthModal(true);
    };

    const openRegister = () => {
        setAuthTab('register');
        setShowAuthModal(true);
    };

    // Default avatar for users without one
    const defaultAvatar = 'https://api.dicebear.com/7.x/avataaars/svg?seed=' + (user?.nickname || 'guest');

    return (
        <header className="sticky top-0 z-50 w-full bg-gradient-header border-b border-primary-dark shadow-nk-lg">
            <div className="max-w-[1400px] mx-auto px-4">
                <div className="flex items-center justify-between h-14">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-1 group">
                        <span className="font-display text-2xl font-bold tracking-tight">
                            <span className="text-accent-green drop-shadow-sm">Klaso</span>
                            <span className="text-accent-yellow drop-shadow-sm">wisko</span>
                        </span>
                        <span className="text-primary-foreground/80 text-xs font-body hidden sm:inline">.pl</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {navItems
                            .filter(item => !item.requiresAuth || isAuthenticated)
                            .map((item) => (
                            <Link
                                key={item.label}
                                to={item.href}
                                className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground transition-colors text-sm font-medium"
                            >
                                <item.icon className="w-4 h-4" />
                                <span className="hidden xl:inline">{item.label}</span>
                                {item.badge && (
                                    <Badge className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] bg-accent-yellow text-foreground border-0">
                                        {item.badge}
                                    </Badge>
                                )}
                            </Link>
                        ))}
                    </nav>

                    {/* Right side actions */}
                    <div className="flex items-center gap-2">
                        {/* Notifications Dropdown */}
                        {isAuthenticated && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="relative text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground"
                                    aria-label={`Powiadomienia${unreadCount > 0 ? ` (${unreadCount} nieprzeczytanych)` : ''}`}
                                >
                                    <Bell className="w-5 h-5" />
                                    {unreadCount > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-accent-yellow text-foreground text-[10px] font-bold flex items-center justify-center">
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </span>
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-80 nk-box p-0">
                                <div className="nk-box-header text-sm flex items-center justify-between">
                                    <span>Powiadomienia</span>
                                    {unreadCount > 0 && (
                                        <button 
                                            onClick={markAllAsRead}
                                            className="text-xs text-primary hover:underline"
                                        >
                                            Oznacz jako przeczytane
                                        </button>
                                    )}
                                </div>
                                <div className="max-h-80 overflow-y-auto">
                                    {notifications.length === 0 ? (
                                        <div className="p-4 text-center text-muted-foreground text-sm">
                                            Brak powiadomień
                                        </div>
                                    ) : (
                                        notifications.map((notif) => (
                                            <DropdownMenuItem 
                                                key={notif.id} 
                                                className={`p-3 cursor-pointer ${!notif.read ? 'bg-accent/50' : ''}`}
                                                onClick={() => handleNotificationClick(notif)}
                                            >
                                                <div className="flex gap-3 w-full">
                                                    <span className="text-xl">{notif.icon || getNotificationIcon(notif.type)}</span>
                                                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                                        <span className="text-sm font-medium text-foreground truncate">{notif.title}</span>
                                                        <span className="text-xs text-muted-foreground line-clamp-2">{notif.message}</span>
                                                        <span className="text-xs text-muted-foreground/70">{formatTime(notif.created_at)}</span>
                                                    </div>
                                                    {!notif.read && (
                                                        <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5"></span>
                                                    )}
                                                </div>
                                            </DropdownMenuItem>
                                        ))
                                    )}
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                    className="justify-center text-primary font-medium"
                                    onClick={() => navigate('/powiadomienia')}
                                >
                                    Zobacz wszystkie
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        )}

                        {/* Dark Mode Toggle - Modern Touch */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            className="text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground"
                            title={isDark ? 'Tryb jasny' : 'Tryb ciemny'}
                            aria-label={isDark ? 'Przełącz na tryb jasny' : 'Przełącz na tryb ciemny'}
                        >
                            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </Button>

                        {/* User Avatar Dropdown or Login Button */}
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-primary-foreground/10 transition-colors" data-testid="user-menu-btn" aria-label="Menu użytkownika">
                                        <img 
                                            src={user?.avatar_url || defaultAvatar} 
                                            alt={user?.display_name || user?.nickname || 'User'}
                                            className="w-8 h-8 rounded-lg border-2 border-primary-foreground/30 object-cover"
                                        />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 nk-box p-0">
                                    <div className="nk-box-header text-sm flex items-center gap-2">
                                        <img src={user?.avatar_url || defaultAvatar} alt="" className="w-6 h-6 rounded" />
                                        {user?.display_name || user?.nickname || 'Użytkownik'}
                                    </div>
                                    <div className="p-2">
                                        <DropdownMenuItem asChild className="cursor-pointer">
                                            <Link to="/profile">
                                                <User className="w-4 h-4 mr-2" />
                                                Mój profil
                                                {user?.profile_completion < 50 && (
                                                    <Badge className="ml-auto bg-accent-yellow text-foreground text-[10px]">
                                                        {user?.profile_completion}%
                                                    </Badge>
                                                )}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild className="cursor-pointer">
                                            <Link to="/messages">
                                                <Mail className="w-4 h-4 mr-2" />
                                                Wiadomości
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild className="cursor-pointer">
                                            <Link to="/friends">
                                                <Users className="w-4 h-4 mr-2" />
                                                Znajomi ({user?.friends_count || 0})
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild className="cursor-pointer">
                                            <Link to="/zorki">
                                                <Coins className="w-4 h-4 mr-2 text-amber-500" />
                                                Zorki
                                                <Badge className="ml-auto bg-amber-500 text-white text-[10px]">
                                                    {user?.zorki_balance || 0}
                                                </Badge>
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild className="cursor-pointer">
                                            <Link to="/polecenia">
                                                <Gift className="w-4 h-4 mr-2 text-pink-500" />
                                                Poleć znajomym
                                            </Link>
                                        </DropdownMenuItem>
                                        {user?.nickname === 'admin' && (
                                            <>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem asChild className="cursor-pointer">
                                                    <Link to="/admin" className="text-primary font-medium">
                                                        <Shield className="w-4 h-4 mr-2" />
                                                        Panel Admina
                                                    </Link>
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            className="cursor-pointer text-destructive"
                                            onClick={handleLogout}
                                            data-testid="logout-btn"
                                        >
                                            <LogOut className="w-4 h-4 mr-2" />
                                            Wyloguj
                                        </DropdownMenuItem>
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={openLogin}
                                    className="text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground"
                                    data-testid="login-btn"
                                    aria-label="Zaloguj się"
                                >
                                    <LogIn className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Zaloguj</span>
                                </Button>
                                <Button 
                                    size="sm"
                                    onClick={openRegister}
                                    className="nk-button-green"
                                    data-testid="register-btn"
                                >
                                    Dołącz!
                                </Button>
                            </div>
                        )}

                        {/* Mobile Menu Toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden text-primary-foreground/90 hover:bg-primary-foreground/10"
                            aria-label={mobileMenuOpen ? 'Zamknij menu' : 'Otwórz menu'}
                            aria-expanded={mobileMenuOpen}
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </Button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                    <nav className="lg:hidden py-3 border-t border-primary-foreground/20 animate-fade-in-up">
                        <div className="flex flex-col gap-1">
                            {navItems
                                .filter(item => !item.requiresAuth || isAuthenticated)
                                .map((item) => (
                                <Link
                                    key={item.label}
                                    to={item.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center gap-3 px-3 py-2 rounded-md text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground transition-colors"
                                >
                                    <item.icon className="w-5 h-5" />
                                    <span className="font-medium">{item.label}</span>
                                    {item.badge && (
                                        <Badge className="ml-auto bg-accent-yellow text-foreground border-0">
                                            {item.badge}
                                        </Badge>
                                    )}
                                </Link>
                            ))}
                            {!isAuthenticated && (
                                <div className="flex gap-2 mt-2 px-3">
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => { setMobileMenuOpen(false); openLogin(); }}
                                        className="flex-1"
                                    >
                                        Zaloguj
                                    </Button>
                                    <Button 
                                        size="sm"
                                        onClick={() => { setMobileMenuOpen(false); openRegister(); }}
                                        className="flex-1 nk-button-green"
                                    >
                                        Dołącz!
                                    </Button>
                                </div>
                            )}
                        </div>
                    </nav>
                )}
            </div>

            {/* Auth Modal */}
            <AuthModal 
                isOpen={showAuthModal} 
                onClose={() => setShowAuthModal(false)} 
                defaultTab={authTab}
            />
        </header>
    );
};
